import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  ActivityResponse,
  AuthConfigResponse,
  Command,
  CommandListResponse,
  type CreateCommandRequest,
  DeleteHistoryResponse,
  Device,
  LoginResponse,
  RecoveryCodesResponse,
  TwoFactorSetupResponse,
  DeviceListResponse,
  LocationHistoryResponse,
  PairingCodeResponse,
  ReverseGeocodeResponse,
  SessionResponse,
  Settings,
  type ChangePasswordRequest,
  type DisableTwoFactorRequest,
  type LoginCodeRequest,
  type LoginRequest,
  type RegisterRequest,
  type UpdateSettingsRequest,
} from '@findmy/shared';
import { ApiRequestError, api, apiNoContent, setCsrfToken } from './client';

export const keys = {
  session: ['session'] as const,
  authConfig: ['auth-config'] as const,
  devices: ['devices'] as const,
  address: (lat: number, lon: number) => ['address', lat, lon] as const,
  commands: (deviceId: string) => ['commands', deviceId] as const,
  /** Every cached range for a device starts with this, so one call refreshes them all. */
  history: (deviceId: string) => ['history', deviceId] as const,
  historyRange: (deviceId: string, from: number, to: number) => ['history', deviceId, from, to] as const,
  settings: ['settings'] as const,
  activity: ['activity'] as const,
};

/** Puts a command into its device's cached list (newest first), replacing an older copy. */
export function upsertCommand(queryClient: QueryClient, command: Command): void {
  queryClient.setQueryData<Command[]>(keys.commands(command.deviceId), (list) =>
    list
      ? [command, ...list.filter((c) => c.id !== command.id)].sort((a, b) =>
          b.createdAt.localeCompare(a.createdAt),
        )
      : list,
  );
}

/** The logged-in session, or null when logged out. */
export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: async ({ signal }) => {
      try {
        const session = await api('/auth/session', SessionResponse, { signal });
        setCsrfToken(session.csrfToken);
        return session;
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 401) {
          setCsrfToken(null);
          return null;
        }
        throw err;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

/** Forget the session locally (after logout, or when the server says it has ended). */
export function endSession(queryClient: QueryClient): void {
  setCsrfToken(null);
  queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== keys.session[0] });
  queryClient.setQueryData(keys.session, null);
}

export function useAuthConfig() {
  return useQuery({
    queryKey: keys.authConfig,
    queryFn: ({ signal }) => api('/auth/config', AuthConfigResponse, { signal }),
  });
}

function useStartSession() {
  const queryClient = useQueryClient();
  return (session: SessionResponse) => {
    setCsrfToken(session.csrfToken);
    queryClient.setQueryData(keys.session, session);
  };
}

/**
 * Step one of logging in. Resolves with a session, or with a ticket when the account
 * needs a two-factor code next (see useLoginCode).
 */
export function useLogin() {
  const start = useStartSession();
  return useMutation({
    mutationFn: (body: LoginRequest) => api('/auth/login', LoginResponse, { method: 'POST', body }),
    onSuccess: (result) => {
      if ('csrfToken' in result) start(result);
    },
  });
}

export function useLoginCode() {
  const start = useStartSession();
  return useMutation({
    mutationFn: (body: LoginCodeRequest) =>
      api('/auth/login/code', SessionResponse, { method: 'POST', body }),
    onSuccess: start,
  });
}

export function useRegister() {
  const start = useStartSession();
  return useMutation({
    mutationFn: (body: RegisterRequest) => api('/auth/register', SessionResponse, { method: 'POST', body }),
    onSuccess: start,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiNoContent('/auth/logout', { method: 'POST' }),
    onSettled: () => endSession(queryClient),
  });
}

export function useDevices() {
  return useQuery({
    queryKey: keys.devices,
    queryFn: async ({ signal }) => (await api('/devices', DeviceListResponse, { signal })).devices,
  });
}

function useReplaceDevice() {
  const queryClient = useQueryClient();
  return (device: Device) =>
    queryClient.setQueryData<Device[]>(keys.devices, (list) =>
      list?.map((d) => (d.id === device.id ? device : d)),
    );
}

export function useRenameDevice() {
  const replace = useReplaceDevice();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api(`/devices/${id}`, Device, { method: 'PATCH', body: { name } }),
    onSuccess: replace,
  });
}

export function useRevokeDevice() {
  const replace = useReplaceDevice();
  return useMutation({
    mutationFn: (id: string) => api(`/devices/${id}/revoke`, Device, { method: 'POST' }),
    onSuccess: replace,
  });
}

export function useDeleteDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiNoContent(`/devices/${id}`, { method: 'DELETE' }),
    onSuccess: (_void, id) =>
      queryClient.setQueryData<Device[]>(keys.devices, (list) => list?.filter((d) => d.id !== id)),
  });
}

export function useCreatePairingCode() {
  return useMutation({
    mutationFn: () => api('/pairing/code', PairingCodeResponse, { method: 'POST' }),
  });
}

/**
 * Approximate street address for a position. Rounded to about 110 m, so a device
 * shuffling about doesn't trigger a new lookup every few seconds.
 */
export function useAddress(position: { lat: number; lon: number } | null | undefined) {
  const lat = position ? Math.round(position.lat * 1000) / 1000 : 0;
  const lon = position ? Math.round(position.lon * 1000) / 1000 : 0;
  return useQuery({
    queryKey: keys.address(lat, lon),
    queryFn: ({ signal }) =>
      api(`/geocode/reverse?lat=${lat}&lon=${lon}`, ReverseGeocodeResponse, { signal }),
    enabled: Boolean(position),
    // A moving device keeps showing its last address until the new one arrives.
    placeholderData: keepPreviousData,
    staleTime: (query) => (query.state.data?.status === 'unavailable' ? 60_000 : Infinity),
    gcTime: 60 * 60 * 1000,
    retry: false,
  });
}

/** Recent commands for a device; kept current by the live feed. */
export function useCommands(deviceId: string) {
  return useQuery({
    queryKey: keys.commands(deviceId),
    queryFn: async ({ signal }) =>
      (await api(`/devices/${deviceId}/commands`, CommandListResponse, { signal })).commands,
  });
}

export function useSendCommand(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCommandRequest) =>
      api(`/devices/${deviceId}/commands`, Command, { method: 'POST', body }),
    onSuccess: (command) => upsertCommand(queryClient, command),
  });
}

/** A device's route between two instants (UTC ms), oldest first. */
export function useHistory(deviceId: string | null, from: number, to: number) {
  return useQuery({
    queryKey: keys.historyRange(deviceId ?? '', from, to),
    enabled: deviceId !== null,
    queryFn: ({ signal }) => {
      const query = new URLSearchParams({
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
      });
      return api(`/devices/${deviceId ?? ''}/locations?${query}`, LocationHistoryResponse, { signal });
    },
    // Switching range keeps the old route on screen until the new one arrives.
    placeholderData: keepPreviousData,
  });
}

export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: ({ signal }) => api('/settings', Settings, { signal }),
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSettingsRequest) => api('/settings', Settings, { method: 'PATCH', body }),
    onSuccess: (settings, body) => {
      queryClient.setQueryData(keys.settings, settings);
      void queryClient.invalidateQueries({ queryKey: keys.activity });
      // A shorter retention period deletes history now; switching addresses off hides them.
      if (body.retentionDays !== undefined) void queryClient.invalidateQueries({ queryKey: ['history'] });
      if (body.reverseGeocodeEnabled !== undefined)
        void queryClient.invalidateQueries({ queryKey: ['address'] });
    },
  });
}

export function useActivity() {
  return useQuery({
    queryKey: keys.activity,
    queryFn: async ({ signal }) => (await api('/settings/activity', ActivityResponse, { signal })).events,
  });
}

export function useDeleteHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (password: string) =>
      api('/locations', DeleteHistoryResponse, { method: 'DELETE', body: { password } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['history'] });
      void queryClient.invalidateQueries({ queryKey: keys.devices });
      void queryClient.invalidateQueries({ queryKey: keys.activity });
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (password: string) => apiNoContent('/account', { method: 'DELETE', body: { password } }),
    onSuccess: () => endSession(queryClient),
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ChangePasswordRequest) => apiNoContent('/auth/password', { method: 'POST', body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.activity }),
  });
}

/** Two-factor setup step 1: the password, in exchange for the secret to scan. */
export function useStartTwoFactor() {
  return useMutation({
    mutationFn: (password: string) =>
      api('/auth/2fa/setup', TwoFactorSetupResponse, { method: 'POST', body: { password } }),
  });
}

function useSecurityChange<T, R>(fn: (input: T) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.settings });
      void queryClient.invalidateQueries({ queryKey: keys.activity });
    },
  });
}

/** Step 2: the first code from the app turns it on and returns the recovery codes. */
export const useEnableTwoFactor = () =>
  useSecurityChange((code: string) =>
    api('/auth/2fa/enable', RecoveryCodesResponse, { method: 'POST', body: { code } }),
  );

export const useDisableTwoFactor = () =>
  useSecurityChange((body: DisableTwoFactorRequest) =>
    apiNoContent('/auth/2fa/disable', { method: 'POST', body }),
  );

export const useReplaceRecoveryCodes = () =>
  useSecurityChange((password: string) =>
    api('/auth/2fa/recovery-codes', RecoveryCodesResponse, { method: 'POST', body: { password } }),
  );
